import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { css } from 'styled-components';

import StepByStep from '../../StepByStep';
import EnterCardDetails from './EnterCardDetailsStep';
import EnterBillingAddress from './EnterBillingAddressStep';
// import Status from '../Status';
import {
  ADD_CREDIT_CARD_WIZARD,
  WIZARD,
  WIZARD_DATA,
} from '../../../constants/sessionStorage';
import {
  removeFromSessionStorage,
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from '../../../util/helpers/sessionStorageHelper';
import ModalBody from '../../Modal/ModalBody';
import { selectAddCreditCardError } from '../../../store/selectors/creditCardSelectors';
import {
  closeWizardContent,
  deleteWizardKey,
} from '../../../util/helpers/wizardHelpers';
import BasicModal from '../../BasicModal/BasicModal';

const steps = [EnterCardDetails, EnterBillingAddress];

const uLayoutStyle = css`
  height: 100%;

  form {
    height: 100%;
  }
`;

const AddCreditDebitCardWizard = ({
  isOpen,
  onClose,
  paymentMethods,
  setPaymentMethods,
  handleChangeValue,
  postPaidFlow,
  goToInitialModal,
  setIsNewCardAdded,
  setIsNewACHAdded,
}) => {
  const [wizardData, setWizardData] = useState(
    () => retrieveFromSessionStorage(WIZARD_DATA) || {},
  );
  const addCreditCardError = useSelector(selectAddCreditCardError);
  const [errorMessage, setErrorMessage] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    if (addCreditCardError) {
      const addCreditCardErrorCode =
        addCreditCardError?.response?.data?.Errors[0]?.Code;
      const addCreditCardErrorMessage =
        addCreditCardError?.response?.data?.Errors[0]?.Message;

      const addCreditCardErrorMessageText =
        addCreditCardErrorCode === 'BraintreeCreatePaymentMethodError'
          ? addCreditCardErrorMessage
          : t('creditCard.unsuccessfulMessage');

      setErrorMessage(addCreditCardErrorMessageText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addCreditCardError]);

  if (!isOpen) {
    return null;
  }

  const handleClose = () => {
    onClose();
    if (postPaidFlow) {
      deleteWizardKey(ADD_CREDIT_CARD_WIZARD);
      return;
    }

    removeFromSessionStorage(WIZARD);
    removeFromSessionStorage(WIZARD_DATA);
    closeWizardContent();
  };

  const setDataForWizard = (value) => {
    if (value) {
      if (postPaidFlow) {
        setWizardData({ ...wizardData, creditCard: value });
        storeInSessionStorage(WIZARD_DATA, {
          ...wizardData,
          creditCard: value,
        });
        return;
      }
      setWizardData(value);
      storeInSessionStorage(WIZARD_DATA, value);
    }
  };
  return (
    <>
      <BasicModal
        isOpen={isOpen}
        close={handleClose}
        title={t('creditCard.addACreditOrDebitCard')}
        size="sm"
        avoidClearingStorage
        closeIconVisible
      >
        <ModalBody css={uLayoutStyle}>
          <StepByStep sessionScopeStep={ADD_CREDIT_CARD_WIZARD}>
            {({ goStepForward, goStepBack }) =>
              steps.map((step) =>
                React.createElement(step, {
                  handleBack: goStepBack,
                  handleNext: goStepForward,
                  handleClose,
                  wizardData,
                  setWizardData: setDataForWizard,
                  hasError: !!addCreditCardError,
                  text: !addCreditCardError
                    ? t('creditCard.successfulMessage')
                    : errorMessage,
                  onButtonClick: handleClose,
                  steps: [
                    { label: 'EnterCardDetails', percentage: 50 },
                    { label: 'EnterBillingAddress', percentage: 100 },
                  ],
                  paymentMethods,
                  setPaymentMethods,
                  handleChangeValue,
                  postPaidFlow,
                  goToInitialModal: postPaidFlow ? onClose : goToInitialModal,
                  setIsNewCardAdded,
                  setIsNewACHAdded,
                }),
              )
            }
          </StepByStep>
        </ModalBody>
      </BasicModal>
    </>
  );
};

AddCreditDebitCardWizard.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  paymentMethods: PropTypes.shape(),
  setPaymentMethods: PropTypes.func,
  handleChangeValue: PropTypes.func,
  postPaidFlow: PropTypes.bool,
  goToInitialModal: PropTypes.func,
  setIsNewCardAdded: PropTypes.func,
  setIsNewACHAdded: PropTypes.func,
};

export default AddCreditDebitCardWizard;
